import { useState, useEffect, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { has, isArray } from "lodash";

import { useToast } from "../../hooks/useToast";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { socketConnection } from "../../services/socket";
import moment from "moment";

let interceptorsConfigured = false;

const parseStoredAccessToken = (rawToken) => {
  if (!rawToken) return "";
  try {
    return JSON.parse(rawToken);
  } catch (error) {
    return String(rawToken);
  }
};

const decodeJwtPayload = (token) => {
  try {
    const payload = String(token || "").split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized + "=".repeat((4 - (normalized.length % 4)) % 4);
    const json = atob(padded);
    return JSON.parse(json);
  } catch (error) {
    return null;
  }
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const useAuth = () => {
  const history = useHistory();
  const getPostLoginPath = (profile) =>
    String(profile || "").toLowerCase() === "motoqueiro"
      ? "/motoqueiro"
      : "/tickets";

  const toast = useToast();
  const [isAuth, setIsAuth] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState({});
  const [modulePermissions, setModulePermissions] = useState([]);
  const [modulePermissionsLoaded, setModulePermissionsLoaded] = useState(false);
  const [socket, setSocket] = useState({});
  const sessionRefreshInFlight = useRef(false);

  const refreshModulePermissions = useCallback(async () => {
    if (!user?.id) {
      setModulePermissions([]);
      setModulePermissionsLoaded(false);
      return;
    }

    setModulePermissionsLoaded(false);
    try {
      const { data } = await api.get("/module-permissions/effective");
      setModulePermissions(Array.isArray(data?.permissions) ? data.permissions : []);
    } catch (_) {
      // Falhar fechado evita expor opções de módulos enquanto a autorização não é conhecida.
      setModulePermissions([]);
    } finally {
      setModulePermissionsLoaded(true);
    }
  }, [user?.id]);

  useEffect(() => {
    refreshModulePermissions();
  }, [refreshModulePermissions]);

  useEffect(() => {
    if (interceptorsConfigured) return;

    const clearSession = () => {
      localStorage.removeItem("token");
      api.defaults.headers.Authorization = undefined;
      setIsAuth(false);
      setUser({});
      setModulePermissions([]);
      setModulePermissionsLoaded(false);
      if (history.location.pathname !== "/login") {
        history.push("/login");
      }
    };

    const reqId = api.interceptors.request.use(
      (config) => {
        const tokenStorage = localStorage.getItem("token");
        if (tokenStorage) {
          const parsedToken = parseStoredAccessToken(tokenStorage);
          config.headers["Authorization"] = `Bearer ${parsedToken}`;
          setIsAuth(true);
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const resId = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config || {};
        const status = error?.response?.status;
        const backendError = error?.response?.data?.error;
        const backendErrorText =
          typeof backendError === "string" ? backendError : "";
        const isRefreshEndpoint =
          typeof originalRequest.url === "string" &&
          originalRequest.url.includes("/auth/refresh_token");
        const isPermissionError = backendError === "ERR_NO_PERMISSION";
        const isInvalidTokenError =
          backendErrorText === "ERR_SESSION_EXPIRED" ||
          /invalid token/i.test(backendErrorText);
        const isAuthError = status === 401 || isInvalidTokenError;
        const shouldAttemptRefresh =
          isAuthError && !isRefreshEndpoint && !isPermissionError;

        if (shouldAttemptRefresh && !originalRequest._retry) {
          if (isRefreshing) {
            return new Promise(function (resolve, reject) {
              failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
                return api(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const { data } = await api.post("/auth/refresh_token");
            if (data) {
              localStorage.setItem("token", JSON.stringify(data.token));
              api.defaults.headers.Authorization = `Bearer ${data.token}`;
              originalRequest.headers["Authorization"] = `Bearer ${data.token}`;
              processQueue(null, data.token);
              return api(originalRequest);
            }
          } catch (e) {
            processQueue(e, null);
            clearSession();
            return Promise.reject(e);
          } finally {
            isRefreshing = false;
          }
        }

        if (isAuthError && !isRefreshEndpoint) {
          clearSession();
        }

        return Promise.reject(error);
      }
    );

    interceptorsConfigured = true;

    return () => {
      api.interceptors.request.eject(reqId);
      api.interceptors.response.eject(resId);
      interceptorsConfigured = false;
    };
  }, [history]);

  useEffect(() => {
    const tokenStorage = localStorage.getItem("token");
    (async () => {
      if (tokenStorage) {
        const accessToken = parseStoredAccessToken(tokenStorage);
        try {
          const { data } = await api.post("/auth/refresh_token");

          moment.locale("pt-br");
          let dueDate;
          if (data.user.company.id === 1) {
            dueDate = "2999-12-31T00:00:00.000Z";
          } else {
            dueDate = data.user.company.dueDate;
          }

          const vencimento = moment(dueDate).format("DD/MM/yyyy");
          const before = moment(moment().format()).isBefore(dueDate);

          if (before === false) {
            localStorage.setItem("companyDueDate", vencimento);
            localStorage.removeItem("token");
            api.defaults.headers.Authorization = undefined;
            setIsAuth(false);
            setLoading(false);
            history.push("/financeiro-aberto");
            return;
          }

          api.defaults.headers.Authorization = `Bearer ${data.token}`;
          setIsAuth(true);
          setUser(data.user);
          window.dispatchEvent(new Event("auth:session-restored"));
        } catch (err) {
          try {
            if (accessToken) {
              api.defaults.headers.Authorization = `Bearer ${accessToken}`;
              const payload = decodeJwtPayload(accessToken);
              const userId = Number(payload?.id);

              if (userId) {
                const { data: userData } = await api.get(`/users/${userId}`);

                moment.locale("pt-br");
                const dueDate =
                  userData?.company?.id === 1
                    ? "2999-12-31T00:00:00.000Z"
                    : userData?.company?.dueDate;
                const vencimento = moment(dueDate).format("DD/MM/yyyy");
                const before = moment(moment().format()).isBefore(dueDate);

                if (before === false) {
                  localStorage.setItem("companyDueDate", vencimento);
                  localStorage.removeItem("token");
                  api.defaults.headers.Authorization = undefined;
                  setIsAuth(false);
                  history.push("/financeiro-aberto");
                  setLoading(false);
                  return;
                }

                localStorage.setItem("companyDueDate", vencimento);
                localStorage.setItem("token", JSON.stringify(accessToken));
                setIsAuth(true);
                setUser(userData);
                window.dispatchEvent(new Event("auth:session-restored"));
                setLoading(false);
                return;
              }
            }
          } catch (fallbackError) {
            localStorage.removeItem("token");
            api.defaults.headers.Authorization = undefined;
            setIsAuth(false);
            setUser({});
          }
          toastError(err, toast);
        }
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (Object.keys(user).length && user.id > 0) {
      let io;
      if (!Object.keys(socket).length) {
        io = socketConnection({ user });
        setSocket(io);
      } else {
        io = socket;
      }

      io.on(`company-${user.companyId}-user`, (data) => {
        if (data.action === "update" && data.user.id === user.id) {
          const permissionsChanged =
            data.user.profile !== user.profile ||
            data.user.allTicket !== user.allTicket ||
            data.user.allUserChat !== user.allUserChat ||
            data.user.allHistoric !== user.allHistoric ||
            data.user.allowGroup !== user.allowGroup ||
            data.user.spyTicket !== user.spyTicket ||
            data.user.allowSendAudio !== user.allowSendAudio ||
            data.user.allowSendMedia !== user.allowSendMedia ||
            data.user.allowViewAllClosedTickets !== user.allowViewAllClosedTickets ||
            data.user.allowContacts !== user.allowContacts ||
            data.user.allowAutomations !== user.allowAutomations ||
            data.user.allowCampaigns !== user.allowCampaigns ||
            data.user.allowAgendaCare !== user.allowAgendaCare ||
            data.user.allowOdontoCare !== user.allowOdontoCare ||
            data.user.allowPetCare !== user.allowPetCare ||
            data.user.allowCallcenterUser !== user.allowCallcenterUser ||
            data.user.allowCallcenterAdmin !== user.allowCallcenterAdmin ||
            data.user.allowCashierPdv !== user.allowCashierPdv ||
            data.user.allowPdv !== user.allowPdv ||
            data.user.allowErpPhone !== user.allowErpPhone ||
            data.user.allowViewAllSchedules !== user.allowViewAllSchedules ||
            data.user.allowAttendance !== user.allowAttendance ||
            data.user.allowEmail !== user.allowEmail ||
            data.user.allowKanban !== user.allowKanban ||
            data.user.allowTags !== user.allowTags ||
            data.user.allowInternalPortal !== user.allowInternalPortal ||
            data.user.allowTasks !== user.allowTasks ||
            data.user.allowMyWallet !== user.allowMyWallet ||
            data.user.allowSchedules !== user.allowSchedules ||
            data.user.allowBarber !== user.allowBarber ||
            data.user.allowInternalChat !== user.allowInternalChat ||
            data.user.allowComidaNaPorta !== user.allowComidaNaPorta ||
            data.user.allowCampaignsList !== user.allowCampaignsList ||
            data.user.allowEmailCampaigns !== user.allowEmailCampaigns ||
            data.user.allowContactLists !== user.allowContactLists ||
            data.user.allowAnnouncements !== user.allowAnnouncements ||
            data.user.allowApi !== user.allowApi ||
            data.user.allowKnowledgeBase !== user.allowKnowledgeBase ||
            data.user.allowUsers !== user.allowUsers ||
            data.user.allowUserProfile !== user.allowUserProfile ||
            data.user.allowQueues !== user.allowQueues ||
            data.user.allowTalkIa !== user.allowTalkIa ||
            data.user.allowAiAgents !== user.allowAiAgents ||
            data.user.allowIntegrations !== user.allowIntegrations ||
            data.user.allowAsaas !== user.allowAsaas ||
            data.user.allowWalletManagement !== user.allowWalletManagement ||
            data.user.allowFinanceiro !== user.allowFinanceiro ||
            data.user.allowSettings !== user.allowSettings;

          setUser((prevUser) => ({
            ...prevUser,
            ...data.user,
          }));
          refreshModulePermissions();

          if (permissionsChanged) {
            toast.info(
              i18n.t("Suas permissões foram atualizadas pelo administrador.")
            );
          }
        }
      });

      return () => {
        io.off(`company-${user.companyId}-user`);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id, user.companyId, refreshModulePermissions]);

  const handleLogin = async (userData) => {
    setLoading(true);

    try {
      const { data } = await api.post("/auth/login", userData);
      const {
        user: { company },
      } = data;

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "campaignsEnabled"
        );
        if (setting && setting.value === "true") {
          localStorage.setItem("cshow", "true");
        } else {
          localStorage.removeItem("cshow");
        }
      }

      if (
        has(company, "companieSettings") &&
        isArray(company.companieSettings[0])
      ) {
        const setting = company.companieSettings[0].find(
          (s) => s.key === "sendSignMessage"
        );

        const signEnable = setting.value === "enable";

        if (setting && setting.value === "enabled") {
          localStorage.setItem("sendSignMessage", signEnable);
        }
      }
      localStorage.setItem("profileImage", data.user.profileImage);

      moment.locale("pt-br");
      let dueDate;
      if (data.user.company.id === 1) {
        dueDate = "2999-12-31T00:00:00.000Z";
      } else {
        dueDate = data.user.company.dueDate;
      }
      const vencimento = moment(dueDate).format("DD/MM/yyyy");

      var diff = moment(dueDate).diff(moment(moment()).format());

      var before = moment(moment().format()).isBefore(dueDate);
      var dias = moment.duration(diff).asDays();

      if (before === true) {
        localStorage.setItem("token", JSON.stringify(data.token));
        localStorage.setItem("companyDueDate", vencimento);
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
        setUser(data.user);
        setIsAuth(true);
        window.dispatchEvent(new Event("auth:login-success"));
        toast.success(i18n.t("auth.toasts.success"));
        if (Math.round(dias) < 5) {
          toast.warning(
            `Sua assinatura vence em ${Math.round(dias)} ${
              Math.round(dias) === 1 ? "dia" : "dias"
            } `
          );
        }

        history.push(getPostLoginPath(data?.user?.profile));
        setLoading(false);
      } else {
        localStorage.setItem("companyDueDate", vencimento);
        localStorage.removeItem("token");
        api.defaults.headers.Authorization = undefined;
        setIsAuth(false);
        setLoading(false);
        history.push("/financeiro-aberto");
      }
    } catch (err) {
      toastError(err, toast);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);

    try {
      await api.delete("/auth/logout");
      if (socket && typeof socket.disconnect === "function") {
        socket.disconnect();
      }
      setSocket({});
      setIsAuth(false);
      setUser({});
      setModulePermissions([]);
      setModulePermissionsLoaded(false);
      localStorage.removeItem("token");
      localStorage.removeItem("cshow");
      api.defaults.headers.Authorization = undefined;
      window.dispatchEvent(new Event("auth:logout"));
      setLoading(false);
      history.push("/login");
    } catch (err) {
      toastError(err, toast);
      setLoading(false);
    }
  };

  const getCurrentUserInfo = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      return data;
    } catch (_) {
      return null;
    }
  }, []);

  const refreshSessionSilently = useCallback(async () => {
    if (sessionRefreshInFlight.current) return;
    const tokenStorage = localStorage.getItem("token");
    if (!tokenStorage) return;

    sessionRefreshInFlight.current = true;

    try {
      const { data } = await api.post("/auth/refresh_token");
      if (data?.token) {
        localStorage.setItem("token", JSON.stringify(data.token));
        api.defaults.headers.Authorization = `Bearer ${data.token}`;
      }
      if (data?.user) {
        setIsAuth(true);
        setUser(data.user);
      }
      localStorage.setItem("lastSessionRefreshAt", String(Date.now()));
    } catch (_) {
      // Mantem comportamento atual: o logout forcado segue no interceptor em caso de 401.
    } finally {
      sessionRefreshInFlight.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isAuth) return undefined;

    const SESSION_REFRESH_INTERVAL_MS = 15 * 60 * 1000;

    const intervalId = window.setInterval(() => {
      refreshSessionSilently();
    }, SESSION_REFRESH_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [isAuth, refreshSessionSilently]);

  return {
    isAuth,
    user,
    modulePermissions,
    modulePermissionsLoaded,
    refreshModulePermissions,
    loading,
    handleLogin,
    handleLogout,
    getCurrentUserInfo,
    socket,
  };
};

export default useAuth;
